// src/context/AuthContext.jsx
import { createContext, useContext, useEffect, useState, useCallback } from "react";
import * as authApi from "../api/auth";
import api, { tokenStore, registerAuthFailureHandler } from "../lib/apiClient";

const AuthContext = createContext(null);

// Pulls { accessToken, refreshToken, partner } out of a login response no
// matter which of the common backend shapes it comes in as. This is a
// stop-gap so login works today — once we see the real shape in the console
// log below, we should replace this with a single direct destructure.
function extractSession(raw) {
  const candidates = [
    raw,
    raw?.data,
    raw?.data?.data,
  ].filter(Boolean);

  for (const c of candidates) {
    const accessToken = c.accessToken || c.token || c.access_token;
    if (accessToken) {
      const refreshToken = c.refreshToken || c.refresh_token;
      const partner =
        c.partner || c.user || c.profile || c.data?.partner || null;
      return { accessToken, refreshToken, partner };
    }
  }
  return { accessToken: null, refreshToken: null, partner: null };
}

export function AuthProvider({ children }) {
  const [partner, setPartner] = useState(() => tokenStore.getPartner());
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    const token = tokenStore.getAccessToken();
    if (!token) return false;

    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const exp = payload.exp * 1000;
      return Date.now() < exp;
    } catch {
      return false;
    }
  });
  const [loading, setLoading] = useState(true);

  const hardLogout = useCallback(() => {
    tokenStore.clear();
    setPartner(null);
    setIsAuthenticated(false);
  }, []);

  useEffect(() => {
    registerAuthFailureHandler(hardLogout);
  }, [hardLogout]);

  useEffect(() => {
    async function bootstrap() {
      const token = tokenStore.getAccessToken();

      if (!token) {
        setLoading(false);
        return;
      }

      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        const exp = payload.exp * 1000;
        const now = Date.now();

        if (now >= exp) {
          hardLogout();
          setLoading(false);
          return;
        }

        // Set the token in the axios instance
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

        const userData = await authApi.getMe();

        setPartner(userData);
        tokenStore.setSession({ partner: userData });
        setIsAuthenticated(true);
      } catch (error) {
        if (error?.response?.status === 401) {
          hardLogout();
        } else {
        }
      } finally {
        setLoading(false);
      }
    }
    bootstrap();
  }, []);

const login = useCallback(async (username, password, turnstileToken) => {
  // Now it accepts 3 parameters
  const response = await authApi.login(username, password, turnstileToken);
  const { accessToken, refreshToken } = extractSession(response);

  if (!accessToken) {
    throw new Error('No access token received');
  }

  tokenStore.setSession({ accessToken, refreshToken });
  api.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
  setIsAuthenticated(true);

  const fullPartner = await authApi.getMe();
  setPartner(fullPartner);
  tokenStore.setSession({ partner: fullPartner });
  return fullPartner;
}, []);
  const logout = useCallback(async () => {
    try {
      const refreshToken = tokenStore.getRefreshToken();
      if (refreshToken) {
        await authApi.logout(refreshToken);
      }
    } catch {
      // ignore network errors on logout
    } finally {
      hardLogout();
    }
  }, [hardLogout]);

  const refreshProfile = useCallback(async () => {
    const data = await authApi.getMe();
    setPartner(data);
    tokenStore.setSession({ partner: data });
    return data;
  }, []);

  return (
    <AuthContext.Provider
      value={{
        partner,
        isAuthenticated,
        loading,
        login,
        logout,
        refreshProfile
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}